<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <vl-layer-vector :visible="layer.visible" :extent="imageExtent" :update-while-interacting="false"
    style="color: white;">

    <vl-source-vector ref="olSource" :loader-factory="loaderFactory" :strategy-factory="strategyFactory" url="-"
      style="color: white;"> <!-- HACK because loader factory not used if URL not specified -->
      <vl-style-func :factory="styleFunctionFactory" />
    </vl-source-vector>

  </vl-layer-vector>
</template>

<script>
import WKT from 'ol/format/WKT';
import { AnnotationCollection, Cytomine } from '@/api';
import { annotBelongsToLayer } from '@/utils/annotation-utils';
import { get } from '@/utils/store-helpers';
import viewerImageContext from './mixins/viewer-image-context';

export default {
  name: 'annotation-layer',
  mixins: [viewerImageContext],
  props: {
    index: String,
    layer: Object
  },
  data() {
    return {
      format: new WKT(),

      cachedAnnotsById: new Map(),
      cacheLoaded: false,
      cacheLoading: null,
      loadedCacheKey: null,
      cacheVersion: 0,
      featureRenderQueue: [],
      featureRenderFrame: null,
      featureRenderToken: 0,
      featureRenderBatchSize: 300,
      firstFeatureRenderBatchSize: 500,
      featureRenderComplete: false
    };
  },
  computed: {
    project: get('currentProject/project'),
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    annotsIdsToSelect() {
      return this.imageWrapper.selectedFeatures.annotsToSelect.map(annot => annot.id);
    },
    imageExtent() {
      return [0, 0, this.image.width, this.image.height];
    },
    selectedFeatures() {
      return this.imageWrapper.selectedFeatures.selectedFeatures;
    },
    ongoingEdit() {
      return this.imageWrapper.draw.ongoingEdit;
    },
    ontologyTerms() {
      return this.imageWrapper.style.ontologyTerms || [];
    },
    styleFunctionFactory() {
      // Force computed property update when one of those properties change (leading to new style function =>
      // rerendering - see https://github.com/ghettovoice/vuelayers/issues/68#issuecomment-404223423)
      this.imageWrapper.selectedFeatures.selectedFeatures;
      this.imageWrapper.style.layersOpacity;
      for (let termId in this.ontologyTerms) {
        let term = this.ontologyTerms[termId];
        term.visible;
        term.color;
        term.opacity;
      }
      this.imageWrapper.style.displayNoTerm;
      this.imageWrapper.style.noTermOpacity;
      this.imageWrapper.properties.selectedPropertyKey;
      this.imageWrapper.properties.selectedPropertyColor;
      this.imageWrapper.review.reviewMode;
      this.imageWrapper.style.wrappedTracks.forEach(track => {
        track.color;
      });

      return () => {
        return this.$store.getters[this.imageModule + 'genStyleFunction'];
      };
    },
    reviewMode() {
      return this.imageWrapper.review.reviewMode;
    },
    showAlgo() {
      return this.imageWrapper.showAlgo !== undefined ? this.imageWrapper.showAlgo : true;
    },
    cacheKey() {
      return [
        this.image.id,
        this.layer.id,
        this.sliceIds.join(','),
        this.showAlgo,
        this.reviewMode
      ].join('|');
    }
  },
  watch: {
    cacheKey() {
      this.clearAnnotationCache();
      this.clearFeatures(false);
      if (this.layer.visible) {
        this.loader();
      }
    },
    'layer.visible'(visible) {
      if (visible) {
        this.loader();
      } else {
        this.cancelFeatureRenderQueue();
      }
    }
  },
  methods: {
    clearFeatures(cache = true) {
      if (this.$refs.olSource) {
        this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', { layer: this.layer, cache });
        this.$refs.olSource.clearFeatures();
      }
    },

    clearAnnotationCache() {
      this.cachedAnnotsById = new Map();
      this.cacheLoaded = false;
      this.cacheLoading = null;
      this.loadedCacheKey = null;
      this.cacheVersion++;
      this.cancelFeatureRenderQueue();
    },

    annotBelongsToLayer(annot) {
      return annotBelongsToLayer(annot, this.layer, this.sliceIds);
    },

    addAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot)) {
        this.cacheAnnot(annot);
        this.addOrUpdateFeature(annot);
      }
    },
    selectAnnotationHandler({ annot, index }) {
      if (index === this.index && this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature) {
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        } else {
          this.$store.dispatch(this.imageModule + 'selectFeature', olFeature);
        }
      }
    },
    async reloadAnnotationsHandler({ idImage, clear = false, hard = false } = {}) {
      if (!idImage || idImage === this.image.id) {
        if (clear) {
          this.clearAnnotationCache();
          this.clearFeatures();
        } else if (hard) {
          this.clearAnnotationCache();
          this.clearFeatures(false);
          await this.loader();
        } else {
          this.syncCachedFeaturesToSource();
        }
      }
    },
    reviewAnnotationHandler(annot) {
      if (this.reviewMode) { // if the image is in review mode, reviewed annotation should no longer be displayed on user layer => call delete handler
        this.deleteAnnotationHandler(annot);
      }
    },
    editAnnotationHandler(annot) {
      if (!this.annotBelongsToLayer(annot)) {
        if (this.hasCachedAnnot(annot.id)) {
          this.removeAnnotFromCacheAndSource(annot);
        }
        return;
      }

      this.cacheAnnot(annot);
      this.addOrUpdateFeature(annot);
    },

    replaceTemporaryAnnotationHandler({ tempId, savedAnnot }) {
      if (this.$refs.olSource) {
        let tempFeature = this.$refs.olSource.getFeatureById(tempId);
        if (tempFeature) {
          this.$refs.olSource.removeFeature(tempFeature);
        }

        if (this.annotBelongsToLayer(savedAnnot)) {
          this.cacheAnnot(savedAnnot);
          this.addOrUpdateFeature(savedAnnot);
        }
      }
    },

    annotationSaveFailedHandler({ tempId, annot, error }) {
      if (this.$refs.olSource) {
        let tempFeature = this.$refs.olSource.getFeatureById(tempId);
        if (tempFeature) {
          let featureAnnot = tempFeature.get('annot');
          if (featureAnnot) {
            featureAnnot.saveFailed = true;
            featureAnnot.errorMessage = error.message || 'Failed to save annotation';
            featureAnnot.originalAnnot = annot;
            tempFeature.set('annot', featureAnnot);
            this.$refs.olSource.changed();
          }
        }
      }
    },

    deleteAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) || this.hasCachedAnnot(annot.id)) {
        this.removeAnnotFromCacheAndSource(annot);
      }
    },

    strategyFactory() {
      return extent => [extent];
    },

    async fetchAnnotations() {
      let annotations = (await Cytomine.instance.api.get(`/annotation-layers/${this.layer.id}/annotations`)).data;
      annotations.forEach((annotation) => {
        annotation.location = decodeURIComponent(atob(annotation.location));
        annotation.term = [];
        annotation.image = this.image.id;
        annotation.project = this.project.id;
      });

      return annotations;
    },
    async fetchAnnots() {
      let annots = await new AnnotationCollection({
        image: this.image.id,
        slices: this.sliceIds,
        showWKT: true,
        showTerm: true,
        showGIS: true,
        showAlgo: this.showAlgo,
      }).fetchAll();

      return annots.array;
    },

    cacheAnnot(annot) {
      this.cachedAnnotsById.set(String(annot.id), annot);
    },

    cacheAnnots(annots) {
      this.cachedAnnotsById = new Map(annots.map(annot => [String(annot.id), annot]));
      this.featureRenderComplete = false;
    },

    hasCachedAnnot(id) {
      return this.cachedAnnotsById.has(String(id));
    },

    getCachedAnnot(id) {
      return this.cachedAnnotsById.get(String(id));
    },

    getCachedAnnots() {
      return Array.from(this.cachedAnnotsById.values());
    },

    getAllSourceFeatures() {
      if (!this.$refs.olSource || !this.$refs.olSource.$source) {
        return [];
      }
      return this.$refs.olSource.$source.getFeatures ? this.$refs.olSource.$source.getFeatures() : [];
    },

    addOrUpdateFeature(annot) {
      if (!this.$refs.olSource) {
        return;
      }
      this.removeQueuedFeature(annot.id);

      let olFeature = this.$refs.olSource.getFeatureById(annot.id);
      if (olFeature) {
        this.updateFeature(olFeature, annot);
      } else {
        this.$refs.olSource.addFeature(this.createFeature(annot));
      }
    },

    removeAnnotFromCacheAndSource(annot) {
      this.cachedAnnotsById.delete(String(annot.id));
      this.removeQueuedFeature(annot.id);

      if (this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (olFeature) {
          this.$refs.olSource.removeFeature(olFeature);
        }
      }

      if (this.selectedFeatures.some(ftr => ftr.id === annot.id)) {
        this.$store.commit(this.imageModule + 'clearSelectedFeatures');
      }
    },

    updateFeature(feature, annot) {
      let indexSelectedFeature = this.selectedFeatures.findIndex(ftr => ftr.id === feature.getId());
      let isFeatureSelected = indexSelectedFeature !== -1;

      if (!annot) {
        console.log(`Removing annot ${feature.getId()} in layer ${this.layer.id} (external action)`);
        this.$refs.olSource.removeFeature(feature);
        if (isFeatureSelected) {
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
        }
        return;
      }

      let storedAnnot = feature.get('annot');
      if (storedAnnot && annot.updated === storedAnnot.updated && this.sameTerms(annot.term, storedAnnot.term)) {
        // no modification performed since feature was loaded
        return;
      }

      if (isFeatureSelected) {
        if (this.ongoingEdit) {
          // if feature is selected and under modification, updating it may lead to conflict
          console.log(`Skipping update of selected annot ${annot.id} in layer ${this.layer.id} (ongoing edit)`);
          return;
        }
        console.log(`Updating selected annot ${annot.id} in layer ${this.layer.id} (external action)`);
        this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', {
          indexFeature: indexSelectedFeature,
          annot
        });
      }

      feature.set('annot', annot);
      feature.setGeometry(this.format.readGeometry(annot.location));
    },

    async loader() {
      if (!this.layer.visible) {
        return;
      }

      let shouldSyncFeatures = false;
      let shouldLoadCache = !this.cacheLoaded || this.loadedCacheKey !== this.cacheKey;

      if (shouldLoadCache) {
        if (!this.cacheLoading) {
          let requestedCacheKey = this.cacheKey;
          let requestedCacheVersion = this.cacheVersion;
          let loadingPromise = this.fetchAllAnnotsForLayer()
            .then(arrayAnnots => {
              if (requestedCacheKey !== this.cacheKey || requestedCacheVersion !== this.cacheVersion) {
                return;
              }
              this.cacheAnnots(arrayAnnots);
              this.cacheLoaded = true;
              this.loadedCacheKey = requestedCacheKey;
              shouldSyncFeatures = true;
            })
            .catch(error => {
              console.log(error);
              this.$notify({ type: 'error', text: this.$t('notif-error-fetch-annotations-viewer') });
            })
            .finally(() => {
              if (this.cacheLoading === loadingPromise) {
                this.cacheLoading = null;
              }
            });
          this.cacheLoading = loadingPromise;
        }
        await this.cacheLoading;
      }

      if (!shouldSyncFeatures && this.cacheLoaded && this.loadedCacheKey === this.cacheKey) {
        shouldSyncFeatures = this.shouldResumeFeatureRender();
      }

      if (shouldSyncFeatures) {
        this.syncCachedFeaturesToSource();
      }
    },

    async fetchAllAnnotsForLayer() {
      if (Object.prototype.hasOwnProperty.call(this.layer, 'username')) {
        return this.fetchAnnots();
      } else {
        return this.fetchAnnotations();
      }
    },

    syncCachedFeaturesToSource() {
      if (!this.$refs.olSource) {
        return;
      }
      this.cancelFeatureRenderQueue();

      let sourceFeatures = this.getAllSourceFeatures();
      let seenAnnots = new Set();
      sourceFeatures.forEach(feature => {
        let id = String(feature.getId());
        seenAnnots.add(id);
        this.updateFeature(feature, this.getCachedAnnot(id));
      });

      let missingAnnots = this.getCachedAnnots().filter(annot => !seenAnnots.has(String(annot.id)));
      if (!missingAnnots.length) {
        this.featureRenderComplete = true;
        return;
      }

      this.featureRenderComplete = false;
      let firstBatchSize = sourceFeatures.length ? this.featureRenderBatchSize : this.firstFeatureRenderBatchSize;
      this.addFeatureBatch(missingAnnots.slice(0, firstBatchSize));
      this.queueFeatureRender(missingAnnots.slice(firstBatchSize));
    },

    loaderFactory() {
      return () => this.loader();
    },

    createFeature(annot) {
      let feature = this.format.readFeature(annot.location);
      feature.setId(annot.id);
      feature.set('annot', annot);

      if (this.annotsIdsToSelect.includes(annot.id)) {
        this.$store.dispatch(this.imageModule + 'selectFeature', feature);
      }

      return feature;
    },

    addFeatureBatch(annots) {
      if (!annots.length || !this.$refs.olSource) {
        return;
      }

      let features = annots
        .filter(annot => this.hasCachedAnnot(annot.id) && !this.$refs.olSource.getFeatureById(annot.id))
        .map(annot => this.createFeature(annot));

      if (!features.length) {
        return;
      }

      if (this.$refs.olSource.addFeatures) {
        this.$refs.olSource.addFeatures(features);
      } else {
        features.forEach(feature => this.$refs.olSource.addFeature(feature));
      }
    },

    queueFeatureRender(annots) {
      this.featureRenderQueue = annots;
      if (this.featureRenderQueue.length) {
        this.featureRenderComplete = false;
        this.scheduleFeatureRender();
      } else {
        this.featureRenderComplete = true;
      }
    },

    scheduleFeatureRender() {
      if (this.featureRenderFrame || !this.featureRenderQueue.length) {
        return;
      }

      let token = this.featureRenderToken;
      let schedule = window.requestAnimationFrame || (callback => window.setTimeout(callback, 16));
      this.featureRenderFrame = schedule(() => {
        this.featureRenderFrame = null;
        if (token !== this.featureRenderToken) {
          return;
        }

        let batch = this.featureRenderQueue.splice(0, this.featureRenderBatchSize);
        this.addFeatureBatch(batch);
        if (this.featureRenderQueue.length) {
          this.scheduleFeatureRender();
        } else {
          this.featureRenderComplete = true;
        }
      });
    },

    cancelFeatureRenderQueue() {
      this.featureRenderToken++;
      this.featureRenderQueue = [];
      if (this.featureRenderFrame) {
        let cancel = window.cancelAnimationFrame || window.clearTimeout;
        cancel(this.featureRenderFrame);
        this.featureRenderFrame = null;
      }
    },

    removeQueuedFeature(id) {
      let stringId = String(id);
      if (this.featureRenderQueue.length) {
        this.featureRenderQueue = this.featureRenderQueue.filter(annot => String(annot.id) !== stringId);
      }
    },

    shouldResumeFeatureRender() {
      if (!this.cachedAnnotsById.size || this.featureRenderQueue.length || this.featureRenderComplete) {
        return false;
      }
      return this.getAllSourceFeatures().length < this.cachedAnnotsById.size;
    },

    sameTerms(terms1, terms2) {
      terms1 = terms1 || [];
      terms2 = terms2 || [];
      if (terms1.length !== terms2.length) {
        return false;
      }
      return terms1.every(term => terms2.includes(term));
    }
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$on('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$on('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationHandler);
    this.$eventBus.$on('replaceTemporaryAnnotation', this.replaceTemporaryAnnotationHandler);
    this.$eventBus.$on('annotationSaveFailed', this.annotationSaveFailedHandler);
  },
  beforeDestroy() {
    this.cancelFeatureRenderQueue();
    // unsubscribe from all events
    this.$eventBus.$off('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$off('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$off('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationHandler);
    this.$eventBus.$off('replaceTemporaryAnnotation', this.replaceTemporaryAnnotationHandler);
    this.$eventBus.$off('annotationSaveFailed', this.annotationSaveFailedHandler);
  }
};
</script>
